import { bloom, collisionSnap, descend, ignition, impact, layer, portalBend, portalPulse, pluck, risingChord, silentGap, sparkle, suction, timerPulse, windTexture } from "@/audio/core/synthArchetypes";
import type { CandidateLayer } from "@/audio/core/types";

/**
 * The exact six candidates the user selected after the PR13-D Round 2
 * listening/blind-test validation pass - the ONLY place this synthesis
 * data is defined. `src/audio/lab/round2/*Candidates.ts` imports these
 * BY REFERENCE to reconstruct their QA display metadata (code/name/
 * description) alongside their two unselected siblings, and
 * `finalMapping.ts` builds production's authoritative event lookup from
 * them directly - neither ever copies the underlying numbers.
 */

/** JACKPOT -> J2 "VACUUM TO REWARD": a very brief inward suction (near-silence, never a gameplay-stopping pause) -> sudden warm harmonic expansion -> one clear high reward accent. */
export const LOCKED_JACKPOT: readonly CandidateLayer[] = [
  layer(0, suction(-5, 150, 0.35), "Inward suction"),
  layer(160, bloom([0, 5, 9], 320, 0.8), "Warm expansion"),
  layer(420, sparkle(14, 200, 0.65), "Reward accent"),
];

/**
 * STARFRUIT -> S3 "SOLAR KEY": a celestial appearance shimmer (QA
 * follow-up - fires once when STARFRUIT first becomes visible, distinct
 * from and quieter/less triumphant than everything after it) -> a lower
 * warm key/unlock impact when eaten -> a rising harmonic beam -> a wide
 * celestial gate opening -> three warnings -> gate closes.
 *
 * "Appearance chime"/"Appearance beacon" are a two-layer pluck+sparkle
 * twinkle (no suction/bloom, so it can never read as JACKPOT's identity;
 * no impact/noise transient, so it never reuses "Key unlock impact"'s own
 * structure) at loudness 0.28/0.42 - clearly audible but well below both
 * JACKPOT's reward accent (0.65) and LEVEL COMPLETE's bright resolution
 * (0.85), per the locked design intent: notice-me-but-don't-outshine-the-
 * real-payoffs.
 */
export const LOCKED_STARFRUIT: readonly CandidateLayer[] = [
  layer(0, pluck(2, 110, 0.28, 0.1), "Appearance chime (twinkle)"),
  layer(100, sparkle(7, 300, 0.42, -0.1), "Appearance beacon (shimmer)"),
  layer(550, impact(-7, 200, 0.4, 0, 900), "Key unlock impact"),
  layer(1050, risingChord([-7, -2, 2, 7], 420, 0.62), "Rising harmonic beam"),
  layer(
    1600,
    bloom([2, 7, 11], 480, 0.68, 0.15, [
      { delayMs: 150, gain: 0.26 },
      { delayMs: 320, gain: 0.13 },
      { delayMs: 480, gain: 0.05 },
    ]),
    "Wide celestial gate opening",
  ),
  // Marks where the real ~12s gate window sits when this story is played as
  // one fixed QA-audition timeline - never consumed positionally by
  // production, which triggers each moment below from its own real event.
  silentGap(2500, "(active window - compressed)"),
  layer(3050, impact(0, 160, 0.4, 0, 1400), "Warning 3"),
  layer(3450, impact(-2, 160, 0.4, 0, 1400), "Warning 2"),
  layer(3850, impact(-4, 160, 0.4, 0, 1400), "Warning 1"),
  layer(4250, descend(2, -9, 400, 0.48), "Gate closes"),
];

/** WORMHOLE -> W2 "VACUUM PORTAL": an inward-suction activation, a very quiet drifting loop, wraps that feel pulled through, and a low, slow-collapsing expiry. */
export const LOCKED_WORMHOLE: readonly CandidateLayer[] = [
  layer(0, suction(6, 260, 0.5, 0), "Activation (suction)"),
  layer(600, suction(0, 500, 0.16, 0), "Active loop (drifting)"),
  layer(1300, suction(8, 260, 0.5, -0.75), "Wrap 1 (pulled left)"),
  layer(1360, portalBend(3, 220, 0.4, 0.75), "Wrap 1 (emerges right)"),
  layer(1800, suction(8, 260, 0.5, 0.75), "Wrap 2 (pulled right)"),
  layer(1860, portalBend(3, 220, 0.4, -0.75), "Wrap 2 (emerges left)"),
  layer(2400, suction(6, 300, 0.45, 0), "Refresh (suction)"),
  layer(3000, portalPulse(-4, 220, 0.32, 0), "Warning pulse 1"),
  layer(3400, portalPulse(-4, 220, 0.32, 0), "Warning pulse 2"),
  layer(3800, portalPulse(-4, 220, 0.32, 0), "Warning pulse 3"),
  layer(4300, descend(-2, -14, 550, 0.35), "Expiry (low collapse)"),
];

/** SPEED TRIAL -> T1 "CLEAN IGNITION": a single crisp ignition, a light centered wind loop, three evenly-spaced dry timer pulses, a confident success tone, and a quick dry exit. */
export const LOCKED_SPEED_TRIAL: readonly CandidateLayer[] = [
  layer(0, ignition(5, 220, 0.55), "Ignition"),
  layer(500, windTexture(500, 0.16, 0, 2200, "highpass", 0.7), "Active loop (light wind)"),
  layer(1200, timerPulse(4, 150, 0.32), "Warning pulse 1"),
  layer(1600, timerPulse(4, 150, 0.32), "Warning pulse 2"),
  layer(2000, timerPulse(4, 150, 0.32), "Warning pulse 3"),
  layer(2500, ignition(9, 300, 0.6), "Success"),
  layer(3100, windTexture(180, 0.2, 0, 2000, "highpass", 0.6), "Exit"),
];

/**
 * Level Complete -> L3 "COMPACT VICTORY" core (completion phrase only -
 * star fills/records are appended by `completion.ts`): a soft warm entry
 * -> two short resolving notes -> a warm closing swell.
 *
 * QA follow-up: manual playtesting found the original 5-layer/690ms core
 * (peak 0.85, ending on a `sparkle` - the same "bright high-register
 * accent" archetype used for reward payoffs) read as a full victory
 * fanfare, too prominent for something heard after every single Campaign
 * level. Softened to a calm, polished confirmation: peak loudness cut to
 * 0.58 (-31.8%), total duration cut to 420ms (-39%), the closing layer
 * moved from `sparkle` (bright, octave-shifted, its own echo tail) to
 * `bloom` (warm harmonic swell, no octave shift) with a single short
 * delay tap instead of `sparkle`'s two, and the three-note ascending
 * arpeggio trimmed to two notes. Still clearly louder than an ordinary
 * bite/click (~0.10-0.125) but now deliberately quieter than every
 * bigger payoff that can follow it in the same completion sequence or
 * elsewhere - NEW STAR RECORD! (0.6), NEW HIGH SCORE! (0.68), and
 * JACKPOT (0.8 peak) - so this routine, always-heard cue never
 * outshines those genuinely rare/special moments. GATE ENTRY (a
 * separate C-base event) is untouched by this change.
 */
export const LOCKED_LEVEL_COMPLETE_CORE: readonly CandidateLayer[] = [
  layer(0, impact(2, 70, 0.34, 0, 1600), "Warm entry"),
  layer(70, pluck(5, 80, 0.38), "Resolution note 1"),
  layer(150, pluck(9, 90, 0.46), "Resolution note 2"),
  layer(240, bloom([2, 7], 180, 0.58, 0, [{ delayMs: 90, gain: 0.12 }]), "Warm resolution"),
];

/** Death -> D2 "SNAP AND FALL": a sharper collision transient followed by a compact downward tail. */
export const LOCKED_DEATH: readonly CandidateLayer[] = [layer(0, collisionSnap(0, 90, 0.55), "Collision transient"), layer(80, descend(-3, -12, 180, 0.42), "Downward tail")];
